<template>
    <div class="custom-tag-container">
        <!-- 标签显示 -->
        <div
            class="custom-tag"
            :class="{
                'active': computedIsActive,
                'has-value': value && value.trim(),
                'show-input': showInput
            }"
            @click="handleTagClick"
        >
            <span class="tag-label">{{ searchValue || label }}</span>

            <!-- 关闭按钮 -->
            <span
                v-if="closable && (searchValue || (value && value.trim()))"
                class="tag-close"
                @click.stop="handleCloseClick"
                title="清除"
            >
                <Icon type="md-close" size="12" />
            </span>
        </div>

        <!-- 输入框 -->
        <Input
            v-if="showInput && mode === 'input'"
            ref="inputRef"
            v-model="inputValue"
            :placeholder="placeholder"
            size="small"
            class="tag-input"
            @blur="handleInputBlur"
            @keyup.enter.native="handleInputEnter"
        />

        <!-- 下拉框 -->
        <Select
            v-if="showInput && mode === 'select'"
            ref="selectRef"
            v-model="inputValue"
            filterable
            :placeholder="placeholder"
            size="small"
            class="tag-input tag-select"
            @on-change="handleSelectChange"
            @on-open-change="handleSelectOpenChange"
        >
            <Option
                v-for="(option, index) in localOptions"
                :key="index"
                :value="option.value"
                :label="option.label"
            >
                {{ option.label }}
            </Option>
        </Select>
    </div>
</template>

<script>
export default {
    name: 'CustomTag',
    props: {
        // 标签名称
        label: {
            type: String,
            default: ''
        },
        // 标签值
        value: {
            type: String,
            default: ''
        },
        // 是否激活
        isActive: {
            type: [Boolean, String],
            default: false,
            validator: value => {
                // 接受布尔值或可转换为布尔值的字符串
                return typeof value === 'boolean' || (typeof value === 'string' && ['true', 'false', ''].includes(value.toLowerCase())) || (typeof value === 'string' && value.trim() === '') || Number.isFinite(Number(value));
            }
        },
        // 是否显示输入框
        showInput: {
            type: Boolean,
            default: false
        },
        // 输入框占位符
        placeholder: {
            type: String,
            default: ''
        },
        // 是否可关闭
        closable: {
            type: Boolean,
            default: true
        },
        // 搜索值（格式化显示）
        searchValue: {
            type: String,
            default: ''
        },
        // 输入模式：'input' | 'select'
        mode: {
            type: String,
            default: 'input',
            validator: value => ['input', 'select'].includes(value)
        },
        // 下拉框选项数据
        options: {
            type: Array,
            default: () => [],
            validator: value => {
                if (!Array.isArray(value)) return false;
                return value.every(option =>
                    option && typeof option === 'object' && 'value' in option && 'label' in option
                );
            }
        },
        // 下拉框选中值
        selectedValue: {
            type: [String, Number],
            default: ''
        }
    },
    data() {
        return {
            inputValue: this.value || '',
            localOptions: this.validateAndFormatOptions(this.options)
        };
    },
    watch: {
        value(newVal) {
            this.inputValue = newVal || '';
        },
        searchValue(newVal) {
            // 当searchValue变化时，更新inputValue
            if (newVal) {
                const parts = newVal.split(':');
                if (parts.length > 1) {
                    this.inputValue = parts[1].trim();
                }
            }
        },
        options: {
            handler(newOptions) {
                this.localOptions = this.validateAndFormatOptions(newOptions);
                console.log('CustomTag options updated:', this.localOptions);
            },
            deep: true,
            immediate: true
        }
    },
    computed: {
        /**
         * 计算是否激活状态，处理各种类型输入
         */
        computedIsActive() {
            if (typeof this.isActive === 'boolean') {
                return this.isActive;
            }

            if (typeof this.isActive === 'string') {
                const trimmed = this.isActive.trim();
                if (trimmed === '' || trimmed.toLowerCase() === 'false') {
                    return false;
                }
                if (trimmed.toLowerCase() === 'true') {
                    return true;
                }
                // 尝试转换为数字
                const num = Number(trimmed);
                if (!isNaN(num)) {
                    return num !== 0;
                }
                // 非空字符串视为true
                return true;
            }

            // 数字类型
            if (Number.isFinite(this.isActive)) {
                return this.isActive !== 0;
            }

            return false;
        }
    },
    methods: {
        /**
         * 验证和格式化选项数据
         * @param {Array} options - 原始选项数据
         * @returns {Array} 格式化后的选项数据
         */
        validateAndFormatOptions(options) {
            if (!Array.isArray(options)) {
                console.warn('CustomTag: options must be an array, received:', typeof options);
                return [];
            }

            return options.map((option, index) => {
                if (!option || typeof option !== 'object') {
                    console.warn(`CustomTag: invalid option at index ${index}:`, option);
                    return { value: String(index), label: `选项 ${index + 1}` };
                }

                return {
                    value: 'value' in option ? String(option.value) : String(index),
                    label: 'label' in option ? String(option.label) : `选项 ${index + 1}`
                };
            });
        },

        /**
         * 处理标签点击事件
         */
        handleTagClick() {
            this.$emit('click');
        },

        /**
         * 处理输入框失焦事件
         */
        handleInputBlur() {
            this.$emit('input-blur', this.inputValue);
        },

        /**
         * 处理回车键事件
         */
        handleInputEnter() {
            this.$emit('input-blur', this.inputValue);
        },

        /**
         * 处理下拉框选择变化事件
         */
        handleSelectChange(value) {
            this.inputValue = value;
            // 安全地获取选中的选项信息
            let selectedOption = null;
            if (Array.isArray(this.localOptions)) {
                selectedOption = this.localOptions.find(option =>
                    String(option.value) === String(value)
                );
            }

            // 如果找到选中项，可以传递更多信息给父组件
            if (selectedOption) {
                this.$emit('input-blur', value, selectedOption);
            }
            else {
                this.$emit('input-blur', value);
            }
        },

        /**
         * 处理下拉框打开/关闭事件
         */
        handleSelectOpenChange(open) {
            // 可以在这里处理下拉框打开/关闭的逻辑
        },

        /**
         * 处理关闭按钮点击事件
         */
        handleCloseClick() {
            this.$emit('close');
        },

        /**
         * 聚焦输入框
         */
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.inputRef) {
                    this.$refs.inputRef.focus();
                }
            });
        }
    },
    mounted() {
        // 如果显示输入框，自动聚焦
        if (this.showInput) {
            this.focusInput();
        }
    }
};
</script>

<style scoped>
.custom-tag-container {
    display: inline-block;
    vertical-align: middle;
}

.custom-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    line-height: 1;
    padding: 0 8px;
    border: 1px solid #e8eaec;
    border-radius: 3px;
    background-color: #f7f7f7;
    font-size: 12px;
    color: #515a6e;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    user-select: none;
    position: relative;
    min-width: 40px;
    text-align: center;
    white-space: nowrap;
    box-sizing: border-box;
}

.custom-tag:hover {
    border-color: #2d8cf0;
    color: #2d8cf0;
    background-color: #f0f8ff;
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 4px 12px rgba(45, 140, 240, 0.25);
    z-index: 1;
}

.custom-tag.active {
    background-color: #2d8cf0;
    border-color: #2d8cf0;
    color: #fff;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(45, 140, 240, 0.3);
}

.custom-tag.active:hover {
    background-color: #57a3f1;
    border-color: #57a3f1;
    color: #fff;
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 4px 12px rgba(87, 163, 241, 0.35);
}

.custom-tag.has-value {
    background: linear-gradient(135deg, #2d8cf0 0%, #19be6b 100%);
    border-color: #2d8cf0;
    color: #fff;
    font-weight: 500;
    box-shadow: 0 3px 10px rgba(45, 140, 240, 0.4);
    border-color: #2d8cf0;
    position: relative;
    overflow: hidden;
    transform: translateY(-1px);
}

.custom-tag.has-value::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
}

.custom-tag.has-value:hover::before {
    left: 100%;
}

.custom-tag.has-value:hover {
    background: linear-gradient(135deg, #19be6b 0%, #2d8cf0 100%);
    border-color: #19be6b;
    color: #fff;
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 16px rgba(25, 190, 107, 0.4);
}

.tag-label {
    display: inline-block;
    vertical-align: middle;
    line-height: 1;
}

.tag-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    margin-left: 4px;
    width: 16px;
    height: 16px;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    opacity: 0.9;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    line-height: 1;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.3);
    color: #fff;
}

.tag-close:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.2);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.tag-input {
    margin-left: 8px;
    width: 120px;
    vertical-align: middle;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 4px;
}

.tag-input:focus {
    border-color: #2d8cf0;
    box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.2);
    outline: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .custom-tag {
        font-size: 11px;
        padding: 0 6px;
        height: 20px;
        line-height: 18px;
        min-width: 35px;
    }

    .tag-input {
        width: 100px !important;
    }

    .tag-close {
        font-size: 10px;
        width: 14px;
        height: 14px;
    }
}

/* 动画效果 */
@keyframes tagPulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
}

.custom-tag.active,
.custom-tag.has-value {
    animation: tagPulse 0.3s ease-in-out;
}
</style>